import { MdAddShoppingCart } from "react-icons/md";
import { useContext, useEffect, useMemo, useState } from "react";
import { DataContext } from "../../lib/contexts";
import { categoryToIcon } from "../../lib/definitions";
import { ShoppingListItemIdAndAmount } from "../../lib/models";
import { MModal } from "../elements/mmodal";
import { MButton } from "../elements/mbutton";
import { callEndpoint } from "../../lib/functions";
import { categoryToColor } from "../../lib/lib";
import {
  ItemCategory,
  UpdateShoppingListAction,
  UpdateShoppingListEndpoint,
} from "@ckal-software/ckal-lib/dist/apps/madplanen";
import { useNotifcation } from "../../lib/hooks/useNotification";

interface AddToShoppingListPopupProps {
  visible: boolean;
  onClose(): void;
  dishIds: string[];
}

export function AddToShoppingListPopup(props: AddToShoppingListPopupProps) {
  const [allIngredients, setAllIngredients] = useState<
    ShoppingListItemIdAndAmount[]
  >([]);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);

  const { dishes, setShoppingList, itemsMap } = useContext(DataContext);
  const notify = useNotifcation();

  useEffect(() => {
    const ingredients: { [name in string]: ShoppingListItemIdAndAmount } = {};

    props.dishIds.forEach((dishId) =>
      (dishes.find((dish) => dish._id === dishId)?.ingredients || []).forEach(
        (ing) => {
          if (!ingredients[ing.id]) {
            ingredients[ing.id] = { _id: ing.id, amount: ing.amount };
          } else {
            ingredients[ing.id].amount += ing.amount;
          }
        },
      ),
    );

    setAllIngredients(Object.values(ingredients));
    setSelectedItemIds(Object.keys(ingredients));
  }, [props.dishIds, dishes]);

  const groupedByCategory = useMemo(() => {
    const groups: Record<string, ShoppingListItemIdAndAmount[]> = {};
    for (const ing of allIngredients) {
      const item = itemsMap[ing._id];
      const cat = item?.category || "Andet";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(ing);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [allIngredients, itemsMap]);

  function onItemClick(item: ShoppingListItemIdAndAmount) {
    if (selectedItemIds.includes(item._id)) {
      setSelectedItemIds((is) => is.filter((id) => id !== item._id));
    } else {
      setSelectedItemIds((is) => is.concat(item._id));
    }
  }

  function onAmountClick(
    e: React.MouseEvent,
    item: ShoppingListItemIdAndAmount,
  ) {
    e.stopPropagation();
    setAllIngredients((ings) =>
      ings.map((ing) =>
        ing._id === item._id ? { ...ing, amount: ing.amount + 1 } : ing,
      ),
    );
  }

  function onAmountLongPress(
    e: React.MouseEvent,
    item: ShoppingListItemIdAndAmount,
  ) {
    e.preventDefault();
    e.stopPropagation();
    setAllIngredients((ings) =>
      ings.map((ing) => (ing._id === item._id ? { ...ing, amount: 1 } : ing)),
    );
  }

  return (
    <MModal
      visible={props.visible}
      onClose={props.onClose}
      title="Overfør til indkøbsliste"
      content={
        <div>
          {groupedByCategory.map(([category, items]) => {
            const color =
              categoryToColor[category as ItemCategory] || "#6b7280";
            return (
              <div
                key={category}
                style={{
                  margin: "10px 0",
                  background: "#fafbfc",
                  borderRadius: 16,
                  overflow: "hidden",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                }}
              >
                {/* Category header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "12px 16px 8px",
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 10,
                      background: `color-mix(in srgb, ${color} 15%, transparent)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 16,
                    }}
                  >
                    {categoryToIcon[category as ItemCategory]}
                  </div>
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 13,
                      letterSpacing: 0.6,
                      color,
                      textTransform: "uppercase",
                    }}
                  >
                    {category}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      color: "#9ca3af",
                      fontWeight: 600,
                    }}
                  >
                    {items.length}
                  </span>
                </div>

                {/* Items */}
                {items.map((i) => {
                  const item = itemsMap[i._id];
                  const checked = selectedItemIds.includes(i._id);
                  return (
                    <div
                      key={i._id}
                      onClick={() => onItemClick(i)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 16px",
                        borderTop: "1px solid #f3f4f6",
                        cursor: "pointer",
                        gap: 16,
                        WebkitTapHighlightColor: "transparent",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 7,
                            border: checked
                              ? "none"
                              : `2px solid color-mix(in srgb, ${color} 25%, transparent)`,
                            background: checked ? color : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            transition: "all 0.2s",
                          }}
                        >
                          {checked && (
                            <span
                              style={{
                                color: "white",
                                fontSize: 12,
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                            >
                              ✓
                            </span>
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: "#4b5563",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.name}
                        </span>
                      </div>
                      <div
                        style={{
                          minWidth: 28,
                          height: 28,
                          borderRadius: 8,
                          background: i.amount !== 1 ? "#eff6ff" : "#f3f4f6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 13,
                          fontWeight: 600,
                          color: i.amount !== 1 ? "#2563eb" : "#6b7280",
                          border: `1px solid ${i.amount !== 1 ? "#bfdbfe" : "#e5e7eb"}`,
                          flexShrink: 0,
                          WebkitTouchCallout: "none",
                          WebkitUserSelect: "none",
                          userSelect: "none",
                        }}
                        onClick={(e) => onAmountClick(e, i)}
                        onContextMenu={(e) => onAmountLongPress(e, i)}
                      >
                        {i.amount}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      }
      footer={
        <div>
          <MButton
            type="primary"
            icon={<MdAddShoppingCart />}
            style={{ width: "100%" }}
            onClick={async () => {
              const [err, data] = await callEndpoint(
                UpdateShoppingListEndpoint,
                {
                  items: allIngredients.filter((ing) =>
                    selectedItemIds.includes(ing._id),
                  ),
                  action: UpdateShoppingListAction.Add,
                },
              );

              if (err) {
                notify("Opdatering af indkøbliste: Der skete en fejl");
                return;
              }

              props.onClose();
              setShoppingList(data);
            }}
            disabled={selectedItemIds.length === 0}
          >
            Overfør
          </MButton>
        </div>
      }
    />
  );
}
